import { useState, useEffect, useRef, useCallback } from 'react'
import './VideoImport.css'
import InstructionsIOS from './InstructionsIOS'
import { useAuth } from '../contexts/AuthContext'
import { searchYoutube, isOpusApiConfigured } from '../services/opusApi'
import { getComposerStats, getComposersByEra, getRandomWorkSuggestion, composerToSlug } from '../services/worksReference'
import { getCachedComposerExerciseCounts, getExercisesByAuthor, getAllExercisesForTeachers } from '../services/exerciseService'
import { getComposerDisplayNameShortFromFamilyName } from '../utils/exerciseDisplay'

/** Slug du nom de famille pour faire matcher référence ("Bach") et exercices ("Johann Sebastian Bach"). */
function refFamilySlug(name) {
  const family = (name || '').trim().split(/\s+/).pop() || name
  return composerToSlug(family)
}

// Fonction pour extraire l'ID YouTube depuis une URL
const extractVideoId = (url) => {
  if (!url) return null
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)
  return match ? match[1] : null
}

// Fonction pour détecter si l'entrée est une URL YouTube valide
const isYouTubeUrl = (input) => {
  if (!input || !input.trim()) return false
  return /(?:youtube\.com|youtu\.be)/.test(input) || /^[a-zA-Z0-9_-]{11}$/.test(input.trim())
}

// Fonction pour encoder les mots-clés pour l'URL de recherche YouTube
const encodeSearchQuery = (query) => {
  return encodeURIComponent(query.trim())
}

// Fonction pour détecter iOS
const detectIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
}

// Simuler la récupération des métadonnées (à remplacer par l'API YouTube Data v3)
const fetchVideoMetadata = async (videoId) => {
  // TODO: Remplacer par un appel réel à l'API YouTube Data v3
  // const response = await fetch(`https://www.googleapis.com/youtube/v3/videos?part=snippet,contentDetails&id=${videoId}&key=YOUR_API_KEY`)
  // const data = await response.json()
  // return data.items[0]
  
  // Simulation avec délai
  await new Promise(resolve => setTimeout(resolve, 800))
  
  return {
    id: videoId,
    snippet: {
      title: 'Vidéo YouTube',
      channelTitle: 'Chaîne YouTube',
      thumbnails: {
        high: {
          url: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
        }
      },
      description: 'Description de la vidéo'
    },
    contentDetails: {
      duration: 'PT5M0S'
    }
  }
}

function VideoImport({ onVideoSelect }) {
  const [inputValue, setInputValue] = useState('')
  const [videoId, setVideoId] = useState(null)
  const [videoMetadata, setVideoMetadata] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isIOS, setIsIOS] = useState(false)
  const [showIOSInstructions, setShowIOSInstructions] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchError, setSearchError] = useState(null)
  const inputRef = useRef(null)
  const apiConfigured = isOpusApiConfigured()

  const [composerStats, setComposerStats] = useState(null)
  const [composersByEra, setComposersByEra] = useState(null)
  const [suggestion, setSuggestion] = useState(null)
  const [selectedEra, setSelectedEra] = useState(null)
  const [countsReady, setCountsReady] = useState(0)
  const exerciseCountsRef = useRef(null)
  const { user, userData } = useAuth()

  const showExploration = !inputValue.trim() && searchResults.length === 0 && !videoMetadata

  useEffect(() => {
    let cancelled = false
    async function load() {
      const [stats, byEra] = await Promise.all([getComposerStats(), getComposersByEra()])
      if (cancelled) return
      let counts = getCachedComposerExerciseCounts()
      if (!counts && user) {
        try {
          if (userData?.role === 'teacher') {
            await getAllExercisesForTeachers()
          } else {
            await getExercisesByAuthor(user.uid)
          }
          counts = getCachedComposerExerciseCounts()
        } catch (e) {
          console.warn('Chargement des exercices pour les stats:', e)
        }
      }
      if (cancelled) return
      exerciseCountsRef.current = counts
      setComposerStats(stats)
      setComposersByEra(byEra)
      setCountsReady(v => v + 1)
      const sug = await getRandomWorkSuggestion(counts)
      if (!cancelled) setSuggestion(sug)
    }
    load()
    return () => { cancelled = true }
  }, [user?.uid, userData?.role])

  const refreshSuggestion = useCallback(async () => {
    const sug = await getRandomWorkSuggestion(exerciseCountsRef.current)
    if (sug) setSuggestion(sug)
  }, [])

  const handleExplorationClick = useCallback((text) => {
    setInputValue(text)
    inputRef.current?.focus()
  }, [])

  const getUnderrepresented = useCallback(() => {
    if (!composerStats) return []
    const counts = exerciseCountsRef.current
    const withCounts = composerStats
      .filter(c => c.worksCount >= 20)
      .map(c => ({
        ...c,
        exerciseCount: counts?.get(refFamilySlug(c.name)) || 0,
        score: (c.worksCount || 0) / ((counts?.get(refFamilySlug(c.name)) || 0) + 1),
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 6)
    if (withCounts.length > 0) return withCounts
    return composerStats
      .sort((a, b) => (b.worksCount || 0) - (a.worksCount || 0))
      .slice(0, 6)
      .map(c => ({
        ...c,
        exerciseCount: counts?.get(refFamilySlug(c.name)) || 0,
        score: (c.worksCount || 0) / ((counts?.get(refFamilySlug(c.name)) || 0) + 1),
      }))
  }, [composerStats])

  const getTopComposersByExercises = useCallback(() => {
    const counts = exerciseCountsRef.current
    if (!counts || !composerStats) return []
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([slug, count]) => {
        const refComposer = composerStats.find(c => refFamilySlug(c.name) === slug)
        const name = refComposer
          ? getComposerDisplayNameShortFromFamilyName(refComposer.name)
          : slug
        return {
          name,
          exerciseCount: count,
          era: refComposer?.era ?? null,
          worksCount: refComposer?.worksCount ?? 0,
        }
      })
  }, [composerStats])

  const exerciseTotals = (() => {
    const counts = exerciseCountsRef.current
    if (!counts) return null
    const totalExercises = [...counts.values()].reduce((s, n) => s + n, 0)
    const coveredComposers = counts.size
    return { totalExercises, coveredComposers }
  })()

  // Détecter iOS au montage du composant
  useEffect(() => {
    const ios = detectIOS()
    setIsIOS(ios)
    
    // Afficher les instructions iOS une seule fois (première fois)
    if (ios) {
      const instructionsShown = localStorage.getItem('opuslab_ios_instructions_shown')
      if (!instructionsShown) {
        setShowIOSInstructions(true)
      }
    }
  }, [])

  // Focus automatique sur le champ au chargement
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  // Détecter les changements dans le champ
  useEffect(() => {
    const trimmedValue = inputValue.trim()
    
    // Réinitialiser l'état si le champ est vide
    if (!trimmedValue) {
      setVideoId(null)
      setVideoMetadata(null)
      setError(null)
      return
    }

    // Vérifier si c'est une URL YouTube valide
    const detectedVideoId = extractVideoId(trimmedValue)
    
    if (detectedVideoId) {
      // C'est une URL YouTube valide
      setError(null)
      if (detectedVideoId !== videoId) {
        setVideoId(detectedVideoId)
        loadVideoMetadata(detectedVideoId)
      }
    } else if (isYouTubeUrl(trimmedValue)) {
      // Format suspect mais pas valide
      setError('Ce lien ne semble pas provenir de YouTube')
      setVideoId(null)
      setVideoMetadata(null)
    } else {
      // Ce n'est pas une URL, c'est probablement une recherche
      setError(null)
      setVideoId(null)
      setVideoMetadata(null)
    }
  }, [inputValue])

  const loadVideoMetadata = async (id) => {
    setIsLoading(true)
    setError(null)
    
    try {
      const metadata = await fetchVideoMetadata(id)
      setVideoMetadata(metadata)
    } catch (err) {
      console.error('Erreur lors du chargement des métadonnées:', err)
      setError('Impossible de charger les informations de la vidéo')
      setVideoMetadata(null)
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    setInputValue(e.target.value)
  }

  const handlePaste = (e) => {
    // Laisser le comportement par défaut, puis le useEffect détectera le changement
  }

  const handlePasteFromClipboard = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.readText) {
        const text = await navigator.clipboard.readText()
        if (text) {
          setInputValue(text)
          // Le useEffect détectera automatiquement si c'est une URL YouTube
        }
      } else {
        // Fallback : focus sur l'input et suggérer le collage manuel
        inputRef.current?.focus()
        setError('Appuyez longuement dans le champ et sélectionnez "Coller"')
        setTimeout(() => setError(null), 3000)
      }
    } catch (err) {
      console.error('Erreur lecture presse-papiers:', err)
      setError('Permission refusée. Veuillez coller manuellement.')
      setTimeout(() => setError(null), 3000)
    }
  }

  const getPlaceholder = () => {
    if (isIOS) {
      return "Appuyez ici et sélectionnez 'Coller' après avoir copié l'URL YouTube"
    }
    return "Collez le lien YouTube ici (Ctrl+V / Cmd+V)"
  }

  const handleSearchOnYouTube = () => {
    const trimmedValue = inputValue.trim()
    
    if (isIOS) {
      // Sur iOS, utiliser m.youtube.com pour tenter de forcer l'ouverture dans Safari
      if (trimmedValue && !isYouTubeUrl(trimmedValue)) {
        const searchQuery = encodeSearchQuery(trimmedValue)
        window.open(`https://m.youtube.com/results?search_query=${searchQuery}`, '_blank')
      } else {
        window.open('https://m.youtube.com', '_blank')
      }
      // Note : iOS peut quand même ouvrir l'app YouTube si elle est installée
      return
    }
    
    // Comportement normal pour Android/Desktop
    if (trimmedValue && !isYouTubeUrl(trimmedValue)) {
      // L'utilisateur a tapé des mots-clés
      const searchQuery = encodeSearchQuery(trimmedValue)
      window.open(`https://www.youtube.com/results?search_query=${searchQuery}`, '_blank')
    } else {
      // Ouvrir YouTube directement
      window.open('https://www.youtube.com', '_blank')
    }
  }

  const handleSearchInApp = async () => {
    const trimmedValue = inputValue.trim()
    if (!trimmedValue || isYouTubeUrl(trimmedValue) || !apiConfigured) return
    setSearchLoading(true)
    setSearchError(null)
    setSearchResults([])
    try {
      const data = await searchYoutube(trimmedValue)
      setSearchResults(data.items || [])
      if (!(data.items?.length)) setSearchError('Aucun résultat. Essayez d\'ouvrir YouTube dans un nouvel onglet.')
    } catch (err) {
      const msg = err?.message || 'Recherche indisponible. Utilisez le bouton « Ouvrir YouTube » si besoin.'
      setSearchError(msg)
      setSearchResults([])
    } finally {
      setSearchLoading(false)
    }
  }

  const handleSelectSearchResult = (item) => {
    if (!item?.id) return
    onVideoSelect({
      id: item.id,
      title: item.title || 'Vidéo YouTube',
      channelTitle: item.channelTitle || '',
      thumbnail: item.thumbnail || `https://img.youtube.com/vi/${item.id}/hqdefault.jpg`,
      duration: 0,
    })
  }

  const handleConfirm = () => {
    if (videoMetadata && videoId) {
      // Parser la durée ISO 8601
      const duration = videoMetadata.contentDetails?.duration || 'PT0M0S'
      const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/)
      let durationSeconds = 0
      if (match) {
        const hours = parseInt(match[1] || 0, 10)
        const minutes = parseInt(match[2] || 0, 10)
        const seconds = parseInt(match[3] || 0, 10)
        durationSeconds = hours * 3600 + minutes * 60 + seconds
      }

      onVideoSelect({
        id: videoId,
        title: videoMetadata.snippet.title,
        channelTitle: videoMetadata.snippet.channelTitle,
        thumbnail: videoMetadata.snippet.thumbnails.high.url,
        duration: durationSeconds
      })
    }
  }

  const getSearchButtonText = () => {
    const trimmedValue = inputValue.trim()
    
    if (trimmedValue && !isYouTubeUrl(trimmedValue)) {
      return `Rechercher "${trimmedValue}" sur YouTube`
    }
    return 'Rechercher sur YouTube ↗'
  }

  const handleCloseIOSInstructions = () => {
    setShowIOSInstructions(false)
    localStorage.setItem('opuslab_ios_instructions_shown', 'true')
  }

  const handleShowIOSInstructions = () => {
    setShowIOSInstructions(true)
  }

  const handleClearInput = () => {
    setInputValue('')
    setSearchResults([])
    setSearchError(null)
    inputRef.current?.focus()
  }

  return (
    <div className="video-import-container">
      <div className="video-import-content">
        {/* Champ principal (Hero Input) */}
        <div className="video-import-input-wrapper">
          <div className="video-import-input-container">
            <svg className="video-import-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onPaste={handlePaste}
              placeholder={getPlaceholder()}
              className={`video-import-input ${error ? 'error' : ''} ${isLoading ? 'loading' : ''}`}
              autoFocus
            />
            {isLoading && (
              <div className="video-import-spinner">
                <div className="spinner"></div>
              </div>
            )}
            {videoMetadata && !isLoading && (
              <div className="video-import-check">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
            )}
            {inputValue.trim() && (
              <button
                type="button"
                className="video-import-clear"
                onClick={handleClearInput}
                title="Vider le champ"
                aria-label="Vider le champ"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
          
          {/* Message d'erreur */}
          {error && (
            <div className="video-import-error">
              {error}
            </div>
          )}
        </div>

        {/* Bouton Coller depuis presse-papiers (iOS uniquement) */}
        {isIOS && (
          <button
            className="ios-paste-btn"
            onClick={handlePasteFromClipboard}
            type="button"
          >
            📋 Coller l'URL YouTube
          </button>
        )}

        {/* Boutons recherche : in-app (si API configurée) + nouvel onglet YouTube */}
        <div className="video-import-search-actions">
          {apiConfigured && inputValue.trim() && !isYouTubeUrl(inputValue.trim()) && (
            <button
              className="video-import-search-btn video-import-search-in-app"
              onClick={handleSearchInApp}
              type="button"
              disabled={searchLoading}
            >
              {searchLoading ? 'Recherche…' : 'Rechercher ici'}
            </button>
          )}
          <button
            className="video-import-search-btn"
            onClick={handleSearchOnYouTube}
            type="button"
          >
            {getSearchButtonText()}
          </button>
        </div>

        {searchError && (
          <div className="video-import-error">{searchError}</div>
        )}

        {/* Résultats de la recherche in-app */}
        {searchResults.length > 0 && (
          <div className="video-import-results">
            <p className="video-import-results-title">Choisissez une vidéo</p>
            <ul className="video-import-results-list">
              {searchResults.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className="video-import-result-card"
                    onClick={() => handleSelectSearchResult(item)}
                  >
                    <div className="video-import-result-thumb">
                      <img src={item.thumbnail || `https://img.youtube.com/vi/${item.id}/mqdefault.jpg`} alt="" />
                    </div>
                    <div className="video-import-result-info">
                      <span className="video-import-result-name">{item.title}</span>
                      {item.channelTitle && <span className="video-import-result-channel">{item.channelTitle}</span>}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {showExploration && composerStats && (
          <div className="vi-explore">
            <div className="vi-explore-stats">
              {exerciseTotals ? (
                <>
                  <span className="vi-explore-stat-block">
                    <span className="vi-explore-stat-value">{exerciseTotals.totalExercises}</span>
                    <span className="vi-explore-stat-label">exercices</span>
                  </span>
                  <span className="vi-explore-stats-sep">&middot;</span>
                  <span className="vi-explore-stat-block">
                    <span className="vi-explore-stat-value">{exerciseTotals.coveredComposers}</span>
                    <span className="vi-explore-stat-label">/{composerStats.length} compositeurs couverts</span>
                  </span>
                  <span className="vi-explore-stats-sep">&middot;</span>
                  <span className="vi-explore-stat-block">
                    <span className="vi-explore-stat-value">~{Math.round(composerStats.reduce((s, c) => s + c.worksCount, 0) / 1000)}k</span>
                    <span className="vi-explore-stat-label">oeuvres r&eacute;f&eacute;renc&eacute;es</span>
                  </span>
                </>
              ) : (
                <>
                  <span className="vi-explore-stat-label">R&eacute;f&eacute;rence&nbsp;: </span>
                  <span>{composerStats.length} compositeurs</span>
                  <span className="vi-explore-stats-sep">&middot;</span>
                  <span>~{Math.round(composerStats.reduce((s, c) => s + c.worksCount, 0) / 1000)}k oeuvres</span>
                </>
              )}
            </div>

            {suggestion && (
              <div className="vi-explore-suggestion">
                <button
                  type="button"
                  className="vi-explore-suggestion-btn"
                  onClick={() => handleExplorationClick(`${suggestion.composer} ${suggestion.workTitle}`)}
                >
                  <span className="vi-explore-suggestion-label">Pourquoi pas&hellip;</span>
                  <span className="vi-explore-suggestion-text">
                    {suggestion.composer} &mdash; {suggestion.workTitle}
                  </span>
                </button>
                <button
                  type="button"
                  className="vi-explore-refresh"
                  onClick={refreshSuggestion}
                  title="Autre suggestion"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10" />
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                  </svg>
                </button>
              </div>
            )}

            {composersByEra && (
              <div className="vi-explore-eras">
                <div className="vi-explore-era-pills">
                  {['Baroque', 'Classique', 'Romantique', 'Moderne'].map(era => (
                    <button
                      key={era}
                      type="button"
                      className={`vi-explore-era-pill ${selectedEra === era ? 'active' : ''}`}
                      onClick={() => setSelectedEra(prev => prev === era ? null : era)}
                    >
                      {era}
                      <span className="vi-explore-era-count">{composersByEra[era]?.length || 0}</span>
                    </button>
                  ))}
                </div>
                {selectedEra && composersByEra[selectedEra] && (
                  <div className="vi-explore-era-composers">
                    {composersByEra[selectedEra].slice(0, 8).map(c => {
                      const exCount = exerciseCountsRef.current?.get(refFamilySlug(c.name))
                      return (
                        <button
                          key={c.slug}
                          type="button"
                          className="vi-explore-composer-chip"
                          onClick={() => handleExplorationClick(c.name)}
                        >
                          <span className="vi-explore-chip-name">{c.name}</span>
                          <span className="vi-explore-chip-meta">
                            {c.worksCount} oeuvres{exCount != null ? ` \u00b7 ${exCount} ex.` : ''}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {(() => {
              const topComposers = getTopComposersByExercises()
              if (topComposers.length === 0) return null
              return (
                <div className="vi-explore-top">
                  <p className="vi-explore-section-title">Compositeurs les plus travaill&eacute;s</p>
                  <div className="vi-explore-top-list">
                    {topComposers.map((c, i) => (
                      <button
                        key={c.name}
                        type="button"
                        className="vi-explore-top-item"
                        onClick={() => handleExplorationClick(c.name)}
                      >
                        <span className="vi-explore-top-rank">{i + 1}</span>
                        <div className="vi-explore-top-info">
                          <div className="vi-explore-top-header">
                            <span className="vi-explore-top-name">{c.name}</span>
                            {c.era && <span className="vi-explore-underrep-era">{c.era}</span>}
                          </div>
                          <span className="vi-explore-top-meta">
                            {c.exerciseCount} exercice{c.exerciseCount !== 1 ? 's' : ''}
                            {c.worksCount > 0 ? ` \u00b7 ${c.worksCount} oeuvres ref.` : ''}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )
            })()}

            <div className="vi-explore-underrep">
              <p className="vi-explore-section-title">Enrichir la base</p>
              <div className="vi-explore-underrep-list">
                {getUnderrepresented().map(c => {
                  const hasCounts = exerciseCountsRef.current != null
                  const pct = hasCounts ? Math.min(100, Math.round((c.exerciseCount / Math.max(c.worksCount, 1)) * 100 * 20)) : 0
                  return (
                    <button
                      key={c.slug}
                      type="button"
                      className="vi-explore-underrep-item"
                      onClick={() => handleExplorationClick(c.name)}
                    >
                      <div className="vi-explore-underrep-header">
                        <span className="vi-explore-underrep-name">{getComposerDisplayNameShortFromFamilyName(c.name)}</span>
                        {c.era && <span className="vi-explore-underrep-era">{c.era}</span>}
                      </div>
                      <div className="vi-explore-underrep-info">
                        {hasCounts
                          ? `${c.worksCount} oeuvres \u00b7 ${c.exerciseCount} exercice${c.exerciseCount !== 1 ? 's' : ''}`
                          : `${c.worksCount} oeuvres r\u00e9f\u00e9renc\u00e9es`}
                      </div>
                      {hasCounts && (
                        <div className="vi-explore-underrep-bar">
                          <div className="vi-explore-underrep-bar-fill" style={{ width: `${pct}%` }} />
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        )}

        {/* Bouton Aide iOS */}
        {isIOS && (
          <button
            className="ios-help-btn"
            onClick={handleShowIOSInstructions}
            type="button"
          >
            ❓ Aide
          </button>
        )}

        {/* Instructions iOS */}
        {isIOS && showIOSInstructions && (
          <InstructionsIOS
            onPaste={handlePasteFromClipboard}
            onClose={handleCloseIOSInstructions}
          />
        )}

        {/* Zone de prévisualisation */}
        {videoMetadata && !isLoading && (
          <div className="video-import-preview">
            <div className="video-import-preview-card">
              <div className="video-import-preview-thumbnail">
                <img
                  src={videoMetadata.snippet.thumbnails.high.url}
                  alt={videoMetadata.snippet.title}
                />
              </div>
              <div className="video-import-preview-content">
                <h3 className="video-import-preview-title">
                  {videoMetadata.snippet.title}
                </h3>
                <p className="video-import-preview-channel">
                  {videoMetadata.snippet.channelTitle}
                </p>
                <button
                  className="video-import-confirm-btn"
                  onClick={handleConfirm}
                >
                  Confirmer et Créer l'exercice
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default VideoImport

